import debounce from "lodash/debounce";
import { apiRequest } from "shared/api/apiRequest";
import {
  type ChoicesLazyLoadEvent,
  type ChoicesRestful,
  type QuestionSelectBase,
  Serializer,
  type SurveyModel,
} from "survey-core";
import {
  type SurveyCreatorModel,
  type SurveyInstanceCreatedEvent,
} from "survey-creator-core";
import { getSocietyChoicesQuestionNameContract } from "shared/api/contracts/society/societyId/choices/questionName";
import { type SocietyView } from "shared/mappers/database/society/society";

export type SetupChoicesFromPillarProps = {
  society: SocietyView;
  surveyModel?: SurveyModel;
  surveyCreatorModel?: SurveyCreatorModel;
  baseUrl?: string;
};
export type RegisterChoicesFromPillarPropertiesProps = {
  society: SocietyView;
  baseUrl?: string;
};

/**
 * Registers the "Choices from Pillar" property on the stock question types that
 * support it.
 *
 * This has to run *before* a SurveyModel is constructed from form JSON.
 * SurveyJS drops properties it doesn't know about, so a question authored with
 * `pillar_choices_question_name` loses it during parsing - and with it the
 * `onSetValue` below that points the question at the choices endpoint, leaving
 * it with no choices at all.
 */
export const registerChoicesFromPillarProperties = ({
  society,
  baseUrl,
}: RegisterChoicesFromPillarPropertiesProps): void => {
  //Lazy load dropdown config is used when the dropdown is loaded after a delay
  const lazyLoadDropdownConfig = {
    name: "pillar_choices_question_name",
    displayName: "Choice Name",
    type: "dropdown",
    choices:
      society.choiceCategory?.map((category) => category.questionName ?? "") ??
      [],
    category: "Choices from Pillar",
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSetValue: (surveyElement: QuestionSelectBase, value: string) => {
      const elementType = surveyElement.getType();
      if (
        elementType === "tagbox" ||
        elementType === "checkbox" ||
        elementType === "radiogroup"
      ) {
        //These dont support lazy loading
        const result = {
          url: `${baseUrl ?? ""}/api/v1/society/${
            society.societyId
          }/choices/${value}`, // A RESTful service's URL.
          valueName: "text", // Specifies which field contains choice values.
          titleName: "text", // Specifies which field contains display texts for choice values.
          path: "body,results",
        };
        surveyElement.setPropertyValue("pillar_choices_question_name", value);

        const choicesByUrl: ChoicesRestful = surveyElement.choicesByUrl;

        choicesByUrl.url = result.url;
        choicesByUrl.valueName = result.valueName;
        choicesByUrl.titleName = result.titleName;
        choicesByUrl.path = result.path;

        choicesByUrl.run();
      } else {
        //Everything Else does
        surveyElement.setPropertyValue("pillar_choices_question_name", value);
        surveyElement.setPropertyValue("choicesLazyLoadEnabled", true);
      }
    },
  };

  //Register the properties for the survey creator
  Serializer.addProperty("checkbox", lazyLoadDropdownConfig);
  Serializer.addProperty("radiogroup", lazyLoadDropdownConfig);
  Serializer.addProperty("tagbox", lazyLoadDropdownConfig); //this one doesn't work well with lazy loading...
  Serializer.addProperty("rating", lazyLoadDropdownConfig);
  Serializer.addProperty("ranking", lazyLoadDropdownConfig);
  Serializer.addProperty("dropdown", lazyLoadDropdownConfig);
  Serializer.addProperty("matrixdropdowncolumn", lazyLoadDropdownConfig);
};

export const setupChoicesFromPillar = ({
  society,
  surveyModel,
  surveyCreatorModel,
  baseUrl,
}: SetupChoicesFromPillarProps): void => {
  if (!surveyModel && !surveyCreatorModel) {
    throw new Error(
      "Either surveyModel or surveyCreatorModel must be provided",
    );
  }

  registerChoicesFromPillarProperties({ society, baseUrl });

  //bit confusing here, basically we need to run "setupchoicesfrom pillar"
  //twice for the builder, once for the ... builder, then for preview
  if (surveyCreatorModel) {
    surveyCreatorModel.onSurveyInstanceCreated.add(
      (sender: SurveyCreatorModel, options: SurveyInstanceCreatedEvent) => {
        if (options.area === "designer-tab" || options.area === "preview-tab") {
          setupChoicesFromPillar({
            society: society,
            surveyModel: options.survey as unknown as SurveyModel,
          });
        }
      },
    );
    return;
  }
  const setChoices = async (
    options: ChoicesLazyLoadEvent,
    choiceCategory: string,
  ) => {
    const choices = await apiRequest({
      contract: getSocietyChoicesQuestionNameContract,
      inputs: {
        params: {
          societyId: society.societyId,
          questionName: choiceCategory,
        },
      },
      filters: { text: options.filter },
      pagination: {
        page: options.skip / options.take + 1,
        pageSize: options.take,
      },
    });

    // const choices = await getCustomChoicesCallout({
    //   societyId: society.societyId,
    //   questionName: choiceCategory,
    //   filters: { text: options.filter },
    //   pagination: {
    //     pageSize: options.take,
    //     page: options.skip / options.take + 1,
    //   },
    //   baseUrl: baseUrl,
    // });

    const items = choices.results.map((choice) => choice.text);
    options.setItems(items, choices.totalResults);
  };

  const _debouncedSetChoices = debounce(setChoices, 1000);

  if (surveyModel) {
    surveyModel.onChoicesLazyLoad.add(async (sender, options) => {
      const choiceCategory = options.question.getPropertyValue(
        "pillar_choices_question_name",
      );
      if (!choiceCategory) {
        //Not a pillar choice, so we don't need to do anything
        return;
      }
      options.setItems([], 1);
      _debouncedSetChoices(options, choiceCategory);
    });
  }
};
